import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@project/components/atoms';
import { useGetPartnerSubscriptionsQuery, useGetMagazineSubscriptionQuery } from '@project/queries';
import { partnerService, magazineService } from '@project/services';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MoonLoader } from 'react-spinners';

export const ManageSubscriptions: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, refetch: refetchSubscriptions } = useGetPartnerSubscriptionsQuery();
  const {
    data: magazineSubscription,
    isLoading: isLoadingMagazine,
    refetch: refetchMagazine,
  } = useGetMagazineSubscriptionQuery();

  const { mutateAsync: cancelSubscriptionAsync, isLoading: isCancelling } = useMutation(
    (subscriptionId: string) => partnerService.cancelSubscription(subscriptionId),
    {
      onSuccess: () => {
        toast.success('Subscription cancelled successfully', { duration: 3000 });
        refetchSubscriptions();
      },
      onError: () => toast.error('Could not cancel subscription', { duration: 3000 }),
    },
  );

  const { mutateAsync: cancelMagazineAsync, isLoading: isCancellingMagazine } = useMutation(
    (subscriptionId: string) => magazineService.cancelSubscription(subscriptionId),
    {
      onSuccess: () => {
        toast.success('Magazine subscription cancelled', { duration: 3000 });
        refetchMagazine();
      },
      onError: () => toast.error('Could not cancel subscription', { duration: 3000 }),
    },
  );

  if (isLoading || isLoadingMagazine) {
    return (
      <div className="mt-40 flex justify-center">
        <MoonLoader color="#B58B00" />
      </div>
    );
  }

  const handleCancelSubscription = async (subscriptionId: number) => {
    await cancelSubscriptionAsync(subscriptionId.toString());
  };

  const handleCancelMagazine = async (subscriptionId: number) => {
    await cancelMagazineAsync(subscriptionId.toString());
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <h1 className="text-2xl font-semibold">Manage Subscriptions</h1>
      {(isCancelling || isCancellingMagazine) && (
        <div className="mt-4 flex justify-center">
          <MoonLoader color="#B58B00" size={25} />
        </div>
      )}

      <Card className="mb-2 flex flex-row items-center justify-between p-4">
        <div className="flex flex-col">
          <h2 className="font-semibold">Magazine Subscription</h2>
          {magazineSubscription ? (
            <>
              <p>
                Amount: {magazineSubscription.amount} {magazineSubscription.currency}
              </p>
              <p>Status: Active</p>
              <p>Billing: Monthly</p>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Subscribe for $20 / month to read all paid magazines.
            </p>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          {magazineSubscription ? (
            <Button
              style="text-red-400 border px-4 py-2 rounded border-red-300"
              variant="outlined"
              onClick={() => handleCancelMagazine(magazineSubscription.id)}
            >
              Cancel
            </Button>
          ) : (
            <Button
              style="bg-gold text-white px-4 py-2 rounded"
              onClick={() => navigate('/partner/magazines/subscribe')}
            >
              Subscribe
            </Button>
          )}
        </div>
      </Card>

      {data?.subscriptions.length > 0 &&
        data?.subscriptions.map((subscription: any) => (
          <Card
            key={subscription.id}
            className="mb-4 flex flex-row items-center justify-between p-4"
          >
            <div className="flex flex-col">
              <h2>{subscription.name}</h2>
              <p>
                Amount: {subscription.amount} {subscription.currency}
              </p>
              <p>Status: Active</p>
              <p>Billing: Monthly</p>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                style="text-red-400 border px-4 py-2 rounded border-red-300"
                variant="outlined"
                onClick={() => handleCancelSubscription(subscription.id)}
              >
                Cancel
              </Button>
            </div>
          </Card>
        ))}
    </div>
  );
};
